import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

// Phase 22a — MatchMarket client hooks (fixed-price listings, closed-loop FiguTokens).
// Auctions live in Phase 22b (MatchBid) and are NOT wired here — the MatchBid
// tab is a placeholder in this phase.

// ── Queries ─────────────────────────────────────────────────────────
export function useMarketNearby(enabled = true) {
  return useQuery({
    queryKey: ["market", "nearby"],
    queryFn: async () => (await api.get("/market/listings/nearby")).data,
    enabled,
    staleTime: 30_000,
  });
}

export function useMyListings(enabled = true) {
  return useQuery({
    queryKey: ["market", "me"],
    queryFn: async () => (await api.get("/market/listings/me")).data,
    enabled,
    staleTime: 15_000,
  });
}

export function useListingDetail(id) {
  return useQuery({
    queryKey: ["market", "detail", id],
    queryFn: async () => (await api.get(`/market/listings/${id}`)).data,
    enabled: !!id,
  });
}

// ── Mutations ───────────────────────────────────────────────────────
export function useCreateListing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) =>
      (await api.post("/market/listings", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["market", "nearby"] });
      qc.invalidateQueries({ queryKey: ["market", "me"] });
    },
  });
}

export function usePurchaseListing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, safeZoneId }) =>
      (
        await api.post(`/market/listings/${id}/purchase`, {
          safe_zone_id: safeZoneId ?? null,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["market", "nearby"] });
      qc.invalidateQueries({ queryKey: ["market", "me"] });
      qc.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });
}

export function useCancelListing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) =>
      (await api.patch(`/market/listings/${id}/cancel`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["market", "nearby"] });
      qc.invalidateQueries({ queryKey: ["market", "me"] });
    },
  });
}

// Photo upload — presigned Tigris PUT. On 503 the modal degrades to text-only.
export async function requestUploadUrl(filename, contentType) {
  const r = await api.post("/market/photos/upload-url", {
    filename,
    content_type: contentType,
  });
  return r.data;
}

export async function uploadPhotoToTigris(file, uploadUrl, contentType) {
  const r = await fetch(uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": contentType },
  });
  if (!r.ok) throw new Error(`upload failed: ${r.status}`);
}

// ── Drop-zone payload contract ──────────────────────────────────────
export const DRAG_MIME = "application/x-figumatch-listing";

export function parseDropPayload(dataTransfer) {
  const raw =
    dataTransfer.getData(DRAG_MIME) ||
    dataTransfer.getData("text/plain") ||
    "";
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && Number.isFinite(parsed.number)) return parsed;
  } catch (_) {
    /* ignore */
  }
  return null;
}
